import React from 'react';
import { useFormContext } from 'react-hook-form';
import SchedulingUserSelector from 'app/components/SchedulingUserSelector';
import { schedulingFetchGet } from 'app/utils/request/scheduling/fetch';
import { uniqBy } from 'lodash';
import { StudentProfileModel } from 'app/models/scheduling/StudentProfileModel';
import { useTranslation } from 'react-i18next';

type Props = {
  facilityId: string;
  canAddNewAthletes?: boolean;
  showList?: boolean;
  setTotalAthletes?: (count: number) => void;
};

export default function AthleteFilterSelector({ facilityId, canAddNewAthletes, showList, setTotalAthletes }: Props) {
  const {
    control,
    register,
    watch,
  } = useFormContext();

  const { t } = useTranslation();
  const watchStudents = watch('studentProfiles');

  const loadStudents = async (
    inputValue: string,
    _currentOptions: [],
    additional: { page: number },
    newStudents: StudentProfileModel[]
  ) => {
    const exceptIds = watchStudents?.map((s: StudentProfileModel) => s.id) || [];
    const page = additional?.page || 1;

    try {
      const response = await schedulingFetchGet(
        `/facilities/${facilityId}/student_profiles`,
        {
          query: inputValue,
          per_page: 20,
          page,
          except: exceptIds,
        },
        { withHeaders: true }
      );

      const { students, headers } = response;
      const currentPage = Number(headers.get('Current-Page'));
      const totalPages = Number(headers.get('Total-Pages'));
      const totalAthletes = Number(headers.get('Total-Count'));
      const nextPage = currentPage < totalPages ? currentPage + 1 : null;

      const allStudents = uniqBy([...newStudents, ...students], 'id');

      setTotalAthletes(totalAthletes);

      return {
        options: allStudents,
        hasMore: currentPage < totalPages,
        additional: {
          page: nextPage,
        },
      };
    } catch (error) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: null,
        },
      };
    }
  };

  return (
    <SchedulingUserSelector
      {...register('studentProfiles', {
        validate: (selected) => Array.isArray(selected) && selected.length > 0,
      })}
      control={control}
      loadOptions={loadStudents}
      placeholder={t("Select athletes")}
      multiSelect
      canAddNewAthletes={canAddNewAthletes}
      showSelection={showList}
    />
  );
}
